import { useTranslation } from 'react-i18next';

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Cog, ShoppingCart, Target } from 'lucide-react';

import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { BaseImportViewProps, ImportView } from './import-view-props';

interface ViewCouponsInstructionsProps extends BaseImportViewProps {
  selectedFlow: 'simple' | 'service' | 'custom' | null;
  setSelectedFlow: (flow: 'simple' | 'service' | 'custom' | null) => void;
  howToUseTemplate: string[];
  setHowToUseTemplate: (template: string[]) => void;
  couponForm: { name: string };
  onFinish: () => Promise<void>;
  finishing: boolean;
  importView: ImportView;
}

const globalStepsCount = 3;
const servicesStepsCount = 7;
const directSalesStepsCount = 4;

export const ViewCouponsInstructions: React.FC<
  ViewCouponsInstructionsProps
> = ({
  setImportView,
  selectedFlow,
  setSelectedFlow,
  howToUseTemplate,
  setHowToUseTemplate,
  onFinish,
  finishing,
  importView,
}) => {
  const { t } = useTranslation();
  const renderSelect = () => (
    <>
      <DialogHeader>
        <DialogTitle>
          {t('components.import_flow.coupons_instructions.title')}
        </DialogTitle>
        <DialogDescription>
          {t('components.import_flow.coupons_instructions.description')}
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-end text-sm font-medium">
        1/{globalStepsCount}
      </div>
      <div className="mb-4 h-2 w-full rounded-full bg-muted">
        <div className="h-2 w-1/2 rounded-full bg-primary" />
      </div>
      <div className="space-y-4">
        <button
          className="flex w-full items-center gap-4 rounded-lg border p-4 text-left hover:bg-muted"
          onClick={() => {
            setSelectedFlow('simple');
            setHowToUseTemplate([
              t(
                'components.import_flow.coupons_instructions.steps_templates.after_purchase_find_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.copy_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.go_to_partner_website'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.choose_products_and_apply_code'
              ),
            ]);
            setImportView('instructionsBuild');
          }}
        >
          <ShoppingCart className="h-6 w-6" />
          <div className="flex flex-col">
            <div className="text-base font-semibold">
              {t(
                'components.import_flow.coupons_instructions.ecommerce_simple'
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {t(
                'components.import_flow.coupons_instructions.ecommerce_simple_description'
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('components.import_flow.coupons_instructions.steps_count', {
                count: directSalesStepsCount,
              })}
            </div>
          </div>
        </button>
        <button
          className="flex w-full items-center gap-4 rounded-lg border p-4 text-left hover:bg-muted"
          onClick={() => {
            setSelectedFlow('service');
            setHowToUseTemplate([
              t(
                'components.import_flow.coupons_instructions.steps_templates.after_purchase_find_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.copy_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.go_to_website'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.choose_service_experience'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.select_date_time'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.create_profile_and_add_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.receive_confirmation_emails'
              ),
            ]);
            setImportView('instructionsBuild');
          }}
        >
          <Target className="h-6 w-6" />
          <div className="flex flex-col">
            <div className="text-base font-semibold">
              {t(
                'components.import_flow.coupons_instructions.service_experience'
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {t(
                'components.import_flow.coupons_instructions.service_experience_description'
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('components.import_flow.coupons_instructions.steps_count', {
                count: servicesStepsCount,
              })}
            </div>
          </div>
        </button>
        <button
          className="flex w-full items-center gap-4 rounded-lg border p-4 text-left hover:bg-muted"
          onClick={() => {
            setSelectedFlow('custom');
            setHowToUseTemplate([
              t(
                'components.import_flow.coupons_instructions.steps_templates.after_purchase_find_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.copy_code'
              ),
              t(
                'components.import_flow.coupons_instructions.steps_templates.step_three_placeholder'
              ),
            ]);
            setImportView('instructionsBuild');
          }}
        >
          <Cog className="h-6 w-6" />
          <div className="flex flex-col">
            <div className="text-base font-semibold">
              {t('components.import_flow.coupons_instructions.custom')}
            </div>
            <div className="text-sm text-muted-foreground">
              {t(
                'components.import_flow.coupons_instructions.custom_description'
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('components.import_flow.coupons_instructions.variable_steps')}
            </div>
          </div>
        </button>
      </div>
    </>
  );

  const renderBuild = () => (
    <>
      <div className="flex-shrink-0">
        <DialogHeader>
          <DialogTitle>
            {t('components.import_flow.coupons_instructions.configure_steps', {
              type:
                selectedFlow === 'simple'
                  ? t(
                      'components.import_flow.coupons_instructions.ecommerce_simple'
                    )
                  : selectedFlow === 'service'
                    ? t(
                        'components.import_flow.coupons_instructions.service_experience'
                      )
                    : t('components.import_flow.coupons_instructions.custom'),
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'components.import_flow.coupons_instructions.configure_description'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="mb-4 flex justify-end text-sm font-medium">
          2/{globalStepsCount}
        </div>
        <div className="mb-6 h-2 w-full rounded-full bg-muted">
          <div className="h-2 w-2/3 rounded-full bg-primary" />
        </div>
        <h3 className="mb-2 text-base font-semibold">
          {t('components.import_flow.coupons_instructions.journey_steps')}
        </h3>
      </div>
      <div className="flex-grow space-y-4 overflow-y-auto px-4 pb-4">
        {howToUseTemplate.map((step, idx) => {
          const isAuto = idx < 2;
          return (
            <div key={idx} className="space-y-1">
              <div className="text-sm font-semibold">
                {t('components.import_flow.coupons_instructions.step', {
                  number: idx + 1,
                })}{' '}
                {isAuto && (
                  <span className="text-sm text-muted-foreground">
                    {t('components.import_flow.coupons_instructions.automatic')}
                  </span>
                )}
              </div>
              {isAuto ? (
                <div className="rounded-md bg-muted px-3 py-3 text-sm">
                  {step}
                </div>
              ) : (
                <Textarea
                  className="min-h-[44px]"
                  value={step}
                  onChange={(e) =>
                    setHowToUseTemplate(
                      howToUseTemplate.map((s: string, i: number) =>
                        i === idx ? e.target.value : s
                      )
                    )
                  }
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex-shrink-0 border-t pt-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              setHowToUseTemplate([
                ...howToUseTemplate,
                `Étape ${howToUseTemplate.length + 1}`,
              ])
            }
          >
            {t('components.import_flow.coupons_instructions.add_step')}
          </Button>
          {howToUseTemplate.length > 1 && (
            <Button
              variant="outline"
              onClick={() => setHowToUseTemplate(howToUseTemplate.slice(0, -1))}
            >
              {t('components.import_flow.coupons_instructions.remove_last')}
            </Button>
          )}
        </div>
        <div className="mt-4 flex justify-between">
          <Button
            variant="ghost"
            onClick={() => setImportView('instructionsSelect')}
          >
            ← {t('general.previous')}
          </Button>
          <Button onClick={() => setImportView('instructionsPreview')}>
            {t('general.previsualize')} →
          </Button>
        </div>
      </div>
    </>
  );

  const renderPreview = () => (
    <>
      <div className="flex-shrink-0">
        <DialogHeader>
          <DialogTitle>
            {t('components.import_flow.coupons_instructions.preview_title')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'components.import_flow.coupons_instructions.preview_description'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="mb-4 flex justify-end text-sm font-medium">
          3/{globalStepsCount}
        </div>
        <div className="mb-4 flex items-center gap-2">
          <div className="h-2 w-full rounded-full bg-muted">
            <div className="h-2 w-full rounded-full bg-primary" />
          </div>
        </div>
      </div>
      <div className="flex-grow overflow-y-auto px-4 pb-4">
        <div className="rounded-md bg-muted/20 p-4">
          <h4 className="mb-3 text-base font-semibold">
            {t('components.import_flow.coupons_instructions.how_it_works')}
          </h4>
          <div className="rounded-md border bg-white p-4">
            <ul className="space-y-4">
              {howToUseTemplate.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-600 text-sm font-bold text-white">
                    {i + 1}
                  </div>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 border-t pt-4">
        <div className="flex justify-between">
          <Button
            variant="ghost"
            onClick={() => setImportView('instructionsBuild')}
          >
            ← {t('general.modify')}
          </Button>
          <Button
            className="bg-green-600 text-white hover:bg-green-700"
            onClick={onFinish}
            disabled={finishing}
          >
            {finishing ? t('general.saving') : t('general.finish')}
          </Button>
        </div>
      </div>
    </>
  );

  const step =
    importView === 'instructionsSelect'
      ? 'select'
      : importView === 'instructionsBuild'
        ? 'build'
        : 'preview';

  return (
    <>
      {step === 'select' && renderSelect()}
      {step === 'build' && renderBuild()}
      {step === 'preview' && renderPreview()}
    </>
  );
};
